<script lang="ts">
  import { onMount } from "svelte";
  import { api, type LeadListItem } from "../lib/api";
  import { haptic } from "../lib/tg";

  let { stage, onback, onhome }: { stage?: string; onback?: () => void; onhome?: () => void } = $props();

  // Silence thresholds (days). Anything < FRESH_MAX is «Живые», 60-180 «Тревожные»,
  // >=180 «Заброшенные». Заброшенные = кандидаты на массовую архивацию.
  const FRESH_MAX = 60;
  const WARNING_MAX = 180;

  let items = $state<LeadListItem[]>([]);
  let total = $state(0);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let archivingId = $state<number | null>(null);
  let bulkArchiving = $state(false);
  // Default: Live раскрыто, Тревожные/Заброшенные свёрнуты (как и просил Александр).
  // Tap по фишке светофора — «focus mode»: раскрывает только нажатую секцию.
  let showLive = $state(true);
  let showWarning = $state(false);
  let showAbandoned = $state(false);
  let focusedSection = $state<"live" | "warning" | "abandoned" | null>("live");

  function focusSection(s: "live" | "warning" | "abandoned") {
    haptic("light");
    if (focusedSection === s) {
      // повторный tap по той же фишке = свернуть всё (overview-mode)
      focusedSection = null;
      showLive = false; showWarning = false; showAbandoned = false;
      return;
    }
    focusedSection = s;
    showLive = s === "live";
    showWarning = s === "warning";
    showAbandoned = s === "abandoned";
    // Прокрутить к секции (после рендера)
    setTimeout(() => {
      const el = document.getElementById(`section-${s}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  }

  function toggleSection(s: "live" | "warning" | "abandoned") {
    haptic("light");
    if (s === "live") showLive = !showLive;
    if (s === "warning") showWarning = !showWarning;
    if (s === "abandoned") showAbandoned = !showAbandoned;
    focusedSection = null;  // ручной toggle — выходим из focus-mode
  }

  function moneyShort(rub: number): string {
    if (rub >= 1_000_000) return `${(rub / 1_000_000).toFixed(1).replace(/\.0$/, "")}М ₽`;
    if (rub >= 1000) return `${Math.floor(rub / 1000)}к ₽`;
    return `${rub} ₽`;
  }
  function silenceText(d: number): string {
    if (d <= 0) return "сегодня";
    if (d === 1) return "1 день молчит";
    if (d < 5) return `${d} дня молчит`;
    return `${d} дней молчит`;
  }

  const titles: Record<string, string> = {
    measure: "Замеры на неделе",
    contract: "Договоры и оплаты",
    nudge_long: "Дожим вдолгую",
  };
  const title = $derived(stage ? (titles[stage] ?? "Сделки") : "Все активные сделки");

  const live = $derived(items.filter((l) => l.silence_days < FRESH_MAX));
  const warning = $derived(items.filter((l) => l.silence_days >= FRESH_MAX && l.silence_days < WARNING_MAX));
  const abandoned = $derived(items.filter((l) => l.silence_days >= WARNING_MAX));

  async function archiveOne(l: LeadListItem, ev: MouseEvent) {
    ev.preventDefault();
    ev.stopPropagation();
    const tg = (window as any).Telegram?.WebApp;
    const text = `Скрыть «${l.name.slice(0, 60)}» из списков?\n\nВ amoCRM сделка останется.`;
    let ok = false;
    if (tg?.showConfirm) {
      ok = await new Promise<boolean>((resolve) => tg.showConfirm(text, (v: boolean) => resolve(!!v)));
    } else {
      ok = confirm(text);
    }
    if (!ok) return;
    archivingId = l.amo_id;
    haptic("medium");
    try {
      await api.archiveDeal(l.amo_id);
      items = items.filter((x) => x.amo_id !== l.amo_id);
      total = Math.max(0, total - 1);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "ошибка";
      if (tg?.showAlert) tg.showAlert(`Не удалось: ${msg}`);
      else alert(`Не удалось: ${msg}`);
    } finally {
      archivingId = null;
    }
  }

  async function archiveAllAbandoned() {
    const count = abandoned.length;
    if (count === 0) return;
    const tg = (window as any).Telegram?.WebApp;
    const text = `Убрать все ${count} заброшенных сделок (молчат больше ${WARNING_MAX} дней) в архив?\n\nВ amoCRM они останутся, но из этого списка исчезнут.`;
    let ok = false;
    if (tg?.showConfirm) {
      ok = await new Promise<boolean>((resolve) => tg.showConfirm(text, (v: boolean) => resolve(!!v)));
    } else {
      ok = confirm(text);
    }
    if (!ok) return;
    bulkArchiving = true;
    haptic("medium");
    const ids = abandoned.map((l) => l.amo_id);
    let ok_count = 0;
    let fail_count = 0;
    // Chunked parallelism: 5 concurrent archive calls per wave — much faster
    // than fully sequential for 25+ deals, still gentle on the API rate limit
    // (archive endpoint is local DB-only, no amo write, so this is cheap).
    const CHUNK = 5;
    for (let i = 0; i < ids.length; i += CHUNK) {
      const wave = ids.slice(i, i + CHUNK);
      const results = await Promise.allSettled(wave.map((id) => api.archiveDeal(id)));
      results.forEach((r, j) => {
        if (r.status === "fulfilled") {
          ok_count++;
          const id = wave[j];
          items = items.filter((x) => x.amo_id !== id);
          total = Math.max(0, total - 1);
        } else {
          fail_count++;
        }
      });
    }
    bulkArchiving = false;
    const msg = fail_count === 0
      ? `✓ Убрано в архив: ${ok_count}`
      : `Убрано ${ok_count}, не получилось ${fail_count}`;
    if (tg?.showAlert) tg.showAlert(msg);
    else alert(msg);
  }

  onMount(async () => {
    try {
      const r = await api.dealsList(stage);
      items = r.items;
      total = r.total;
    } catch (e) {
      error = e instanceof Error ? e.message : "ошибка";
    } finally {
      loading = false;
    }
  });
</script>

<div class="screen">
  <header class="hdr">
    <button class="back" onclick={() => onback?.()} aria-label="Назад">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
        stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
    </button>
    <div class="title">{title} · {total}</div>
    {#if onhome}
      <button class="home" onclick={() => onhome?.()} aria-label="Главная">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
          stroke-linecap="round" stroke-linejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
        </svg>
      </button>
    {/if}
  </header>

  {#if loading}
    <div class="loading">Загружаю…</div>
  {:else if error}
    <div class="error-box">{error}</div>
  {:else if items.length === 0}
    <div class="empty">Сделок нет</div>
  {:else}
    {#snippet row(l: LeadListItem, opts: { showArchive?: boolean })}
      <div class="row-wrap">
        <a class="row" class:hot={l.silence_days >= 14} href={`?deal=${l.amo_id}`}>
          {#if l.silence_days >= 7}
            <div class="sil-badge" class:hot={l.silence_days >= 14}>
              {l.silence_days}<span class="sil-unit">дн</span>
            </div>
          {/if}
          <div class="row-main">
            <div class="row-top">
              <div class="row-text">{l.name}</div>
              {#if l.price_rub > 0}<div class="row-price">{moneyShort(l.price_rub)}</div>{/if}
            </div>
            <div class="row-meta">
              {l.manager_name} <span class="sep"></span>
              {l.stage_name} <span class="sep"></span>
              {silenceText(l.silence_days)}
            </div>
          </div>
        </a>
        {#if opts.showArchive}
          <button class="arch-btn" type="button" aria-label="В архив"
                  disabled={archivingId === l.amo_id}
                  onclick={(ev) => archiveOne(l, ev)}>
            {#if archivingId === l.amo_id}⋯{:else}🗄{/if}
          </button>
        {/if}
      </div>
    {/snippet}

    <div class="traffic-light">
      <button class="tl-pill green"
              class:active={focusedSection === "live"}
              class:dim={live.length === 0}
              type="button"
              onclick={() => focusSection("live")}>
        <span class="tl-dot"></span>
        <span class="tl-count">{live.length}</span>
        <span class="tl-label">Живые</span>
      </button>
      <button class="tl-pill yellow"
              class:active={focusedSection === "warning"}
              class:dim={warning.length === 0}
              type="button"
              onclick={() => focusSection("warning")}>
        <span class="tl-dot"></span>
        <span class="tl-count">{warning.length}</span>
        <span class="tl-label">Тревожные</span>
      </button>
      <button class="tl-pill red"
              class:active={focusedSection === "abandoned"}
              class:dim={abandoned.length === 0}
              type="button"
              onclick={() => focusSection("abandoned")}>
        <span class="tl-dot"></span>
        <span class="tl-count">{abandoned.length}</span>
        <span class="tl-label">Заброшенные</span>
      </button>
    </div>

    <section class="list">
      <button id="section-live" class="section-toggle live"
              type="button" onclick={() => toggleSection("live")}>
        <span class="section-title">🟢 Живые · {live.length}</span>
        <span class="section-hint">&lt; {FRESH_MAX} дн · {showLive ? "▼" : "▶"}</span>
      </button>
      {#if showLive}
        {#if live.length === 0}
          <div class="section-empty">Нет «живых» сделок — всё молчит</div>
        {:else}
          {#each live as l}
            {@render row(l, { showArchive: false })}
          {/each}
        {/if}
      {/if}

      <button id="section-warning" class="section-toggle warning"
              type="button" onclick={() => toggleSection("warning")}>
        <span class="section-title">🟡 Тревожные · {warning.length}</span>
        <span class="section-hint">{FRESH_MAX}–{WARNING_MAX} дн · {showWarning ? "▼" : "▶"}</span>
      </button>
      {#if showWarning}
        {#if warning.length === 0}
          <div class="section-empty">Нет сделок в этой зоне</div>
        {:else}
          {#each warning as l}
            {@render row(l, { showArchive: true })}
          {/each}
        {/if}
      {/if}

      <button id="section-abandoned" class="section-toggle abandoned"
              type="button" onclick={() => toggleSection("abandoned")}>
        <span class="section-title">🔴 Заброшенные · {abandoned.length}</span>
        <span class="section-hint">≥ {WARNING_MAX} дн · {showAbandoned ? "▼" : "▶"}</span>
      </button>
      {#if showAbandoned}
        {#if abandoned.length === 0}
          <div class="section-empty">Нет заброшенных — отлично</div>
        {:else}
          <div class="bulk-bar">
            <button class="bulk-btn" type="button" disabled={bulkArchiving} onclick={archiveAllAbandoned}>
              {bulkArchiving ? "Убираю…" : `🗄 Убрать все ${abandoned.length} в архив`}
            </button>
          </div>
          {#each abandoned as l}
            {@render row(l, { showArchive: true })}
          {/each}
        {/if}
      {/if}
    </section>
  {/if}
</div>

<style>
  .screen { padding-bottom: 80px; }
  .hdr {
    padding: calc(12px + var(--safe-top, 0px)) 16px 12px;
    display: flex; align-items: center; gap: 8px;
    border-bottom: 1px solid var(--border);
  }
  .back, .home {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    color: var(--text-mute);
  }
  .back:active, .home:active { background: var(--bg-elev); }
  .back svg, .home svg { width: 20px; height: 20px; }
  .title {
    font-family: 'Manrope', sans-serif;
    font-size: 15px; font-weight: 700;
    flex: 1; text-align: center;
  }
  .loading, .error-box, .empty { padding: 40px 16px; text-align: center; color: var(--text-mute); font-size: 13px; }
  .error-box { color: var(--danger); }

  .list { padding: 12px 16px 80px; display: flex; flex-direction: column; gap: 8px; }

  .traffic-light {
    position: sticky;
    top: calc(var(--safe-top, 0px) + 50px);
    z-index: 5;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 8px;
    padding: 10px 16px;
    background: var(--bg);
    border-bottom: 1px solid var(--border);
  }
  .tl-pill {
    background: var(--bg-elev);
    border: 1.5px solid var(--border-strong, var(--border));
    border-radius: 14px;
    padding: 8px 6px;
    display: flex; flex-direction: column; align-items: center;
    gap: 2px;
    cursor: pointer;
    transition: transform 80ms ease, background 120ms, border-color 120ms;
    font-family: 'Manrope', sans-serif;
    color: var(--text);
    -webkit-tap-highlight-color: transparent;
  }
  .tl-pill:active { transform: scale(0.96); }
  .tl-pill.dim { opacity: 0.45; }
  .tl-pill.active {
    background: var(--bg-elev-2);
    border-width: 2px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.18);
  }
  .tl-pill.green.active { border-color: #2bb673; }
  .tl-pill.yellow.active { border-color: #d8a32c; }
  .tl-pill.red.active { border-color: var(--danger); }
  .tl-dot {
    width: 10px; height: 10px; border-radius: 50%;
    margin-bottom: 2px;
  }
  .tl-pill.green .tl-dot { background: #2bb673; box-shadow: 0 0 6px rgba(43,182,115,0.4); }
  .tl-pill.yellow .tl-dot { background: #d8a32c; box-shadow: 0 0 6px rgba(216,163,44,0.4); }
  .tl-pill.red .tl-dot { background: var(--danger); box-shadow: 0 0 6px rgba(213,77,77,0.4); }
  .tl-count {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 700; font-size: 18px;
    line-height: 1.1;
  }
  .tl-label {
    font-size: 10px;
    color: var(--text-mute);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .section-toggle {
    margin-top: 4px;
    background: var(--bg-elev);
    border: 1px dashed var(--border-strong, var(--border));
    border-radius: 12px;
    padding: 11px 14px;
    display: flex; align-items: center; justify-content: space-between;
    gap: 10px;
    color: var(--text);
    cursor: pointer;
    text-align: left;
    font-family: 'Manrope', sans-serif;
    -webkit-tap-highlight-color: rgba(232,124,47,0.08);
  }
  .section-toggle:first-child { margin-top: 0; }
  .section-toggle:active { transform: scale(0.99); }
  .section-toggle.live    { border-left: 4px solid #2bb673; }
  .section-toggle.warning { border-left: 4px solid #d8a32c; }
  .section-toggle.abandoned { border-left: 4px solid var(--danger); }
  .section-title {
    font-family: 'Manrope', sans-serif;
    font-weight: 700; font-size: 13px;
    color: var(--text);
  }
  .section-hint {
    font-size: 11px; color: var(--text-mute);
    flex-shrink: 0;
    font-family: 'JetBrains Mono', monospace;
  }
  .section-empty {
    padding: 16px 14px;
    text-align: center;
    font-size: 12px; color: var(--text-dim);
    font-style: italic;
  }

  .bulk-bar { padding: 4px 0; }
  .bulk-btn {
    width: 100%;
    background: var(--bg-elev-2);
    border: 1.5px solid var(--ember);
    color: var(--ember);
    border-radius: 12px;
    padding: 12px;
    font-family: 'Manrope', sans-serif;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
  }
  .bulk-btn:active { transform: scale(0.98); background: var(--ember); color: #fff; }
  .bulk-btn:disabled { opacity: 0.5; cursor: wait; }

  .row-wrap {
    display: flex; gap: 8px; align-items: stretch;
  }
  .row {
    flex: 1; min-width: 0;
    display: flex; gap: 10px; align-items: center;
    background: var(--bg-elev);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 12px 14px;
    color: var(--text);
    text-decoration: none;
    box-shadow: var(--shadow-sm);
    transition: transform 80ms ease;
  }
  .row:active { transform: scale(0.985); }
  .row.hot { border-left: 3px solid var(--danger); }

  .arch-btn {
    flex-shrink: 0;
    width: 40px;
    align-self: stretch;
    background: var(--bg);
    border: 1.5px solid var(--border-strong, var(--border));
    border-radius: 12px;
    color: var(--text-mute);
    font-size: 18px;
    cursor: pointer;
    transition: all 120ms;
  }
  .arch-btn:active { transform: scale(0.94); }
  .arch-btn:hover { color: var(--text); border-color: var(--ember); }
  .arch-btn:disabled { opacity: 0.5; cursor: wait; }

  .sil-badge {
    flex-shrink: 0;
    min-width: 44px; height: 44px;
    border-radius: 10px;
    background: var(--warning);
    color: #0E0E10;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    font-family: 'JetBrains Mono', monospace;
    font-weight: 700;
    font-size: 15px;
    line-height: 1;
    padding: 0 6px;
  }
  .sil-badge.hot { background: var(--danger); color: #fff; }
  .sil-unit { font-size: 9px; opacity: 0.85; margin-top: 2px; font-weight: 500; }
  .row-main { flex: 1; min-width: 0; }
  .row-top {
    display: flex; justify-content: space-between; align-items: center;
    gap: 10px;
  }
  .row-text {
    font-family: 'Manrope', sans-serif;
    font-weight: 600; font-size: 14px;
  }
  .row-price {
    font-family: 'JetBrains Mono', monospace;
    font-size: 12px; font-weight: 600;
    color: var(--brass);
    flex-shrink: 0;
  }
  .row-meta {
    margin-top: 4px;
    font-size: 11px; color: var(--text-mute);
    display: flex; align-items: center; gap: 8px;
    flex-wrap: wrap;
  }
  .sep { width: 3px; height: 3px; background: var(--text-dim); border-radius: 50%; }
</style>
